import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import useDC from '@eneco/dc/useDC';
import { FlowError, Header, Layout, Overview } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useCustomerProfileAccount } from '@eneco/hooks/profile';
import { Button, NotificationBox, Stack, Text, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import type { ContractExtensionDetailsStepRendering } from '@sitecore/types/ContractExtensionDetailsStep';

import { FlowConsumerContextEnergy } from '../../../context';
import { getExtensionEnergyType, getExtensionUsageValues } from '../../helpers';
import { useFlowNavigation, useCachedOfferMachine } from '../../hooks';

export const ContractExtensionDetailsStep: FC = () => {
  const { businessUnit, label, accountId, customerId } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const { context: flowContent } = flowState;

  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);

  const { ContractExtensionDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    ContractExtensionDetailsStep: ContractExtensionDetailsStepRendering;
  }>();

  const {
    currentAccount,
    data: customerData,
    isValidating: isLoadindAccountDetails,
    error: isFailedAccountDetails,
  } = useCustomerProfileAccount();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const accountData = currentAccount;
      const offerData = offerState.context.offer;
      const usageData = offerData?.usages;

      updateContextAndGoToNextStep({
        customerId,
        accountId,

        salutation: customerData?.person?.salutation ?? undefined,
        initials: customerData?.person?.initials ?? undefined,
        firstName: customerData?.person?.firstName ?? undefined,
        surnamePreposition: customerData?.person?.surnamePreposition ?? undefined,
        surname: customerData?.person?.surname ?? undefined,
        birthdate: customerData?.person?.dateOfBirth ?? undefined,

        postalCode: accountData?.address?.postalCode ?? undefined,
        city: accountData?.address?.city ?? undefined,
        street: accountData?.address?.street ?? undefined,
        houseNumber: Number(accountData?.address?.houseNumber) || undefined,
        houseNumberSuffix: accountData?.address?.houseNumberSuffix ?? undefined,

        phoneNumber: customerData?.contact?.mobilePhoneNumber ?? customerData?.contact?.phoneNumber ?? undefined,
        emailAddress: customerData?.contact?.emailAddress ?? undefined,

        isMoving: false,
        isUsageKnown: true,
        ci: false,

        // Derived from the offer, not the account: an extension offer often covers only part of
        // what the customer has (a gas-only renewal for an electricity + gas customer), and the
        // offer step builds its Sitecore content keys from this.
        energyType: getExtensionEnergyType(offerData, accountData?.productTypes),

        // TODO: figure out how to determine this value
        // hasResidencePurpose: ???

        // Commodities DC reports as `null` land as `undefined` (hidden in the consumption overview),
        // while a reported `0` is kept so the overview shows it.
        ...getExtensionUsageValues(usageData),
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (!isLoadindAccountDetails && !isFailedAccountDetails) {
      sendToOfferMachine({
        type: 'GET_EXTENSION_OFFER',

        flowContext: {
          ...flowContent,
          customerId,
          accountId,
        },
      });
    }
  }, [isLoadindAccountDetails]);

  useEffect(() => {
    if (isFailedAccountDetails) {
      setIsLoading(false);

      setIsFailed(true);
    }
  }, [isFailedAccountDetails]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('GET_EXTENSION'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
    }
  }, [offerState]);

  const getErrorNotification = () => {
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;

    return textualData?.genericError?.errorNotification;
  };

  return (
    <Layout.Content variant="B" isLoading={isLoading}>
      {isSuccessful && (
        <>
          <Header>
            <Header.Title>{textualData?.content?.title?.value}</Header.Title>
            {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
          </Header>
          <Overview>
            <Overview.Header>{textualData?.personalDetailsOverview?.title?.value}</Overview.Header>
            <Overview.Details
              title={textualData?.personalDetailsOverview?.personalDetailsLabel?.value || ''}
              type="profile">
              <Text color="textLowEmphasis" size="BodyS">
                {textualData?.personalDetailsOverview?.nameLabel?.value}: {customerData?.person?.name}
              </Text>
              <Text color="textLowEmphasis" size="BodyS">
                {textualData?.personalDetailsOverview?.phoneNumberLabel?.value}:{' '}
                {customerData?.contact?.mobilePhoneNumber ?? customerData?.contact?.phoneNumber}
              </Text>
              <Text color="textLowEmphasis" size="BodyS">
                {textualData?.personalDetailsOverview?.emailAddressLabel?.value}: {customerData?.contact?.emailAddress}
              </Text>
            </Overview.Details>
          </Overview>

          {textualData?.content?.notification?.value?.content && (
            <NotificationBox
              isAlert={false}
              variant={textualData?.content?.notification?.value?.variant}
              title={textualData?.content?.notification?.value?.title}
              text={<RichText html={textualData?.content?.notification?.value?.content} />}
            />
          )}

          <Stack direction={{ initial: 'column', md: 'row' }}>
            <Button type="button" isLoading={isSubmitSuccessful} onClick={() => setIsSubmitSuccessful(true)}>
              {textualData?.content?.nextStepText?.value}
            </Button>
          </Stack>

          <TextLink
            emphasis="high"
            href={textualData?.content?.portalLink?.value?.href}
            target={textualData?.content?.portalLink?.value?.target}>
            {textualData?.content?.portalLink?.value?.text}
          </TextLink>
        </>
      )}

      {isFailed && (
        <FlowError
          type="GENERIC"
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: !isNotAvailable && !isNotFound ? textualData?.genericError?.tryAgainButtonText : undefined,
          }}
        />
      )}
    </Layout.Content>
  );
};
